import { type FormInstance, Form } from 'antd';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  type AtlasGqlGlobalWorkContainerField,
  AtlasGqlWorkContainerTypeOption,
} from 'types/atlas-graphql';
import { translateStandardFieldName } from 'utils/standardWorkFieldName';

import { TaskFormInput } from '../helpers/TaskFormInput';

interface Props {
  fields: AtlasGqlGlobalWorkContainerField[];
  form: FormInstance<any>;
  isRepairOrInspect: (type: string) => boolean;
  type: AtlasGqlWorkContainerTypeOption;
  uneditableFields: Record<string, React.ReactNode>;
  assetId?: string;
  formItemLayout?: object;
}

export function FormInputs({
  fields,
  form,
  isRepairOrInspect,
  type,
  uneditableFields,
  assetId,
  formItemLayout,
}: Props) {
  const { t } = useTranslation();

  function showRules(field: AtlasGqlGlobalWorkContainerField, type: string) {
    return !isRepairOrInspect(type) && field.name === 'Description'
      ? {
          rules: [
            {
              required: true,
              message: t('inspection.enterDescription', 'Please enter a description'),
            },
          ],
        }
      : {};
  }

  const sortedFields = useMemo(() => {
    return [...fields].sort((valA, valB) => {
      const orderA =
        valA?.configured?.find(c => c?.associatedType?.type === type)?.displayOrder ?? 0;
      const orderB =
        valB?.configured?.find(c => c?.associatedType?.type === type)?.displayOrder ?? 0;
      return orderA - orderB;
    });
  }, [fields, type]);

  // These form inputs are also used by the Create Work Order wizard.
  // This variable is needed to help identify which parent container selector to use
  const parentLabel =
    type === AtlasGqlWorkContainerTypeOption.WorkOrder
      ? t('table.campaign', 'Campaign')
      : t('table.workOrder', 'Work Order');

  return sortedFields.map(field => (
    <Form.Item
      key={field.slug}
      label={field.slug === 'parent' ? parentLabel : translateStandardFieldName(field.name, t)}
      name={field.slug}
      {...showRules(field, type)}
      {...formItemLayout}
    >
      <TaskFormInput
        field={field}
        form={form}
        uneditableFields={uneditableFields}
        assetId={assetId}
        containerType={type}
      />
    </Form.Item>
  ));
}
